import React, { useEffect, useMemo, useState } from "react";
import { highnote } from "@highnote/server/src/sdk";
import {
  ARCHIVABLE_ENTITY_TYPES,
  COLLECTION_ID,
  PUBLIC_ID,
  Space,
} from "@highnote/server/src/core/entities";
import { useHistory } from "react-router";
import { ErrorBoundary } from "App/common/ErrorBoundary";
import { Action, Actions } from "App/common/Actions";
import { useConfirmation } from "App/common/useConfirmation";
import { ReactComponent as CopySVG } from "App/common/icons/copy-hollow.svg";
import { ReactComponent as MoveSVG } from "App/common/icons/collection-move.svg";
import { ReactComponent as InfoSVG } from "App/common/icons/track-list.svg";
import { ReactComponent as WaveformSVG } from "App/common/icons-v2/waveform.svg";
import { ReactComponent as DeleteSVG } from "App/common/icons/trash.svg";
import { ReactComponent as RemoveSVG } from "App/common/icons/remove.svg";
import { ReactComponent as DownloadSVG } from "App/common/icons/download.svg";
import {
  MAX_STORAGE_SPACE_ERROR,
  MAX_TRACKS_ERROR,
  PERMISSION,
} from "@highnote/server/src/core/shared-util";
import { formatCount } from "App/modules/utils";
import { getAuthId, useAuth } from "App/components/Auth";

import { useToast } from "App/common/useToast";
import { useUrlContext } from "App/routes/Main/useUrlContext";
import { useTrackEditor } from "App/components/useTrackEditor";
import { useTrack } from "../useTrack";
import {
  TrackFileDownloadType,
  useEntityDownload,
} from "App/common/useEntityDownload";
import { useGlobalSpaces } from "App/store/spaces/useGlobalSpaces";
import { useUserDownloadRequests } from "../useDownloadRequests";
import {
  LIMIT_TYPE,
  usePlanLimitsContext,
} from "App/common/PlanLimits/usePlanLimits";
import { ButtonV2Props } from "App/core/ButtonV2";
import { useTrackVersionEditorContext } from "App/components/TrackVersionEditor/utils/context/TrackVersionEditorContext";
import { useMoveSelectionsToTarget } from "../useMoveSelectionsToTarget";
import {
  DEFAULT_TRACK_ACTION,
  getDefaultTrackActions,
  getDeleteTrackErrorMessage,
} from "./trackActions.util";
import type { MenuProps } from "@mui/material/Menu";
import {
  RenderableTrack,
  isCanonicalTrack,
} from "App/store/tracks/buildSpaceTrackTile";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import { ViewingSpaceId } from "App/modules/viewingSpace";

export const TrackActions = ({
  track,
  actions,
  parentSpace,
  viewingSpaceId: viewingSpaceIdProp,
  buttonProps,
  menuProps,
  hideTriggerButton,
}: {
  track: RenderableTrack;
  actions: (DEFAULT_TRACK_ACTION | Action)[];
  parentSpace?: Space;
  /**
   * The space the track is being acted on FROM — the list it was rendered
   * in (see `EntityData.viewingSpaceId`). Sent as the viewing `spaceId` on
   * delete/remove so the server can refuse a delete from a BORROWING space.
   * Not the route's space: a nested child-space row on a parent's page, or
   * a sidebar row under any route, would then send the wrong space and
   * have a perfectly legitimate delete refused. Omit when the render has no
   * containing space (library, search): the guard then stays silent.
   */
  viewingSpaceId?: ViewingSpaceId;
  buttonProps?: ButtonV2Props;
  menuProps?: Partial<MenuProps>;
  hideTriggerButton?: boolean;
}) => {
  // The caller's value (the list this row sits in) wins; a row-backed tile
  // carries the same space itself.
  const viewingSpaceId = viewingSpaceIdProp ?? track.viewingSpaceId;
  // Read here, not at the call site below: `isCanonicalTrack(track)` narrows
  // `track` to a plain `Track`, and `Track` has no `isBorrowed`. A row-only
  // tile whose content mirror is complete DOES pass `isCanonicalTrack`, so
  // the two questions are genuinely independent.
  const isBorrowed = track.isBorrowed === true;
  const history = useHistory();
  const { confirm, renderConfirmation } = useConfirmation();
  const { addErrorMessage, toasted } = useToast();
  const url = useUrlContext();
  const { openMoveSelectionsToTarget } = useMoveSelectionsToTarget();
  const { openTrackEditor } = useTrackEditor();
  const { openTrackVersionEditor } = useTrackVersionEditorContext();
  const { trackArtworkUrl } = useTrack();
  const { downloadTrackFiles } = useEntityDownload(parentSpace);
  const { getDownloadRequest } = useUserDownloadRequests();
  const trackDownloadRequest = getDownloadRequest(
    track.id,
    ARCHIVABLE_ENTITY_TYPES.TRACK,
  );
  const { showPlanLimitsDialog } = usePlanLimitsContext();
  const { membersV2Enabled } = useMembershipFlags();

  const [isLoading, setIsLoading] = useState(false);

  const handleDownloadTrackFiles = async (type: TrackFileDownloadType) => {
    // Needs the canonical version/attachment lists. The menu items that
    // reach this are hidden for a row-only tile, so this is a belt-and-braces
    // narrowing rather than the primary gate.
    if (!isCanonicalTrack(track)) return;
    setIsLoading(true);
    await downloadTrackFiles(type, track, trackArtworkUrl);
    setIsLoading(false);
  };

  // Both deleting (creator) and removing (Space owner/manager who isn't the
  // creator) call the same `deleteTrack` callable — the backend hard-deletes
  // for the creator and otherwise moves the Track to the creator's drafts. The
  // only difference here is the confirmation/toast wording: a remove isn't a
  // permanent destruction.
  const deleteOrRemoveTrack = async (isRemove: boolean) => {
    let messageContent: React.ReactNode =
      "This Track is not yet in any Spaces.";

    // The dialog names the space the track is being removed FROM — the one
    // the caller is looking at — never the origin of a borrowed track.
    const dialogSpaceId = viewingSpaceId ?? track.spaceId;
    if (dialogSpaceId) {
      let space: Space;
      try {
        space = await highnote.getSpace({
          id: dialogSpaceId,
        });
      } catch (e) {
        // empty
      }

      if (space) {
        /*
         * The same change as in `SpaceActions.tsx`, minus the caller:
         * `memberCount` includes the caller, and on this path the caller is
         * always a member (they move a track out of a space they can
         * manage). The clamp is there because `memberCount` is a stored
         * counter, and a stored counter can be stale or zero.
         */
        const numPeople = membersV2Enabled
          ? Math.max(0, (space?.memberCount ?? 0) - 1)
          : Object.keys(space?.rolesV2 || {}).filter(
              (u) => u !== PUBLIC_ID && u !== getAuthId(),
            ).length;

        const commentCount = await highnote.getCommentsCount({
          spaceId: space.id,
          trackId: track.id,
        });

        const commentsAffected = commentCount > 0;
        messageContent = (
          <>
            {commentsAffected && (
              <>
                This Track has {formatCount(commentCount, "comment")} in{" "}
                {space.name} which will no longer be visible.{" "}
              </>
            )}
            {commentsAffected ? "It will also" : "This track will"} be removed
            from {space.name}, which is currently shared with{" "}
            {formatCount(numPeople, "other person", "other people")}.
            {isRemove && " The Track will be returned to its owner's drafts."}
          </>
        );
      }
    }

    try {
      await confirm({
        title: isRemove ? `Remove ${track.title}?` : `Delete ${track.title}?`,
        body: (
          <p>
            {messageContent}
            <br />
            <strong>
              Are you sure you want to {isRemove ? "remove" : "delete"}{" "}
              {track.title}?
            </strong>{" "}
            {isRemove ? "" : "This cannot be undone."}
          </p>
        ),
      });

      await toasted({
        promise: highnote.deleteTrack({
          id: track.id,
          ...(viewingSpaceId && { spaceId: viewingSpaceId }),
        }),
        createMessage: isRemove ? `Removing Track...` : `Deleting Track...`,
        successMessage: isRemove ? `Track removed.` : `Track deleted.`,
        ErrorContent: ({ error }) => (
          <>{getDeleteTrackErrorMessage(error, isRemove)}</>
        ),
      });

      // Deleted the track whose page we are on: go up to its space page.
      const pageSpaceId = viewingSpaceId ?? track.spaceId;
      if (url.spaceId === pageSpaceId && url.trackId === track.id) {
        history.push(`/space/${pageSpaceId}`);
      }
    } catch (e) {
      // empty
    }
  };

  const openDetails = () => {
    // The track editor reads the canonical version and attachment lists, so
    // a row-only tile would open it empty with no explanation.
    if (!isCanonicalTrack(track)) return;
    openTrackEditor({ track, viewingSpaceId });
  };

  const duplicateTrack = async () => {
    try {
      const newTrack = await highnote.cloneEntity({
        entityId: track.id,
        entityType: COLLECTION_ID.TRACK,
      });
      openTrackEditor({ track: newTrack });
    } catch (err) {
      if (err.message === MAX_TRACKS_ERROR) {
        showPlanLimitsDialog(LIMIT_TYPE.TRACKS);
        return;
      }
      if (err.message === MAX_STORAGE_SPACE_ERROR) {
        showPlanLimitsDialog(LIMIT_TYPE.STORAGE);
        return;
      }
      addErrorMessage(err.message);
    }
  };

  if (!track) return null;

  return (
    <ErrorBoundary name="CollectionActions">
      <Actions
        buttonProps={buttonProps}
        menuProps={menuProps}
        hideTriggerButton={hideTriggerButton}
        actions={actions.map((action) => {
          if (action === DEFAULT_TRACK_ACTION.SEPARATOR) {
            return { name: "SEPARATOR" };
          }

          if (action === DEFAULT_TRACK_ACTION.DETAILS) {
            // See openDetails: the editor would render empty.
            if (!isCanonicalTrack(track)) return null;
            return {
              name: "Details",
              icon: <InfoSVG />,
              onClick: openDetails,
            };
          }

          if (action === DEFAULT_TRACK_ACTION.DUPLICATE) {
            return {
              name: "Duplicate",
              icon: <CopySVG />,
              onClick: duplicateTrack,
            };
          }

          if (action === DEFAULT_TRACK_ACTION.MOVE_TO_SPACE) {
            return {
              name: "Move to...",
              icon: <MoveSVG />,
              onClick: () => {
                openMoveSelectionsToTarget({
                  selectedEntities: {
                    [track.id]: {
                      entityId: track.id,
                      entityType: COLLECTION_ID.TRACK,
                      parentEntity: {
                        entityId: track.spaceId,
                        entityType: COLLECTION_ID.SPACE,
                      },
                      // The space this row is currently viewed from — not
                      // track.spaceId above (that's the track's ORIGIN, used
                      // only for the unrelated parentEntity bookkeeping).
                      // moveSelectionToEntity doesn't read this field today,
                      // but every TRACK selection carries it for
                      // consistency with the bulk hard-delete guard's
                      // contract on this same field.
                      ...(viewingSpaceId && { spaceId: viewingSpaceId }),
                    },
                  },
                });
              },
            };
          }

          if (action === DEFAULT_TRACK_ACTION.DELETE) {
            return {
              name: "Delete Track",
              icon: <DeleteSVG />,
              onClick: () => deleteOrRemoveTrack(false),
              warn: true,
            };
          }

          if (action === DEFAULT_TRACK_ACTION.REMOVE_FROM_SPACE) {
            return {
              name: "Remove from Space",
              icon: <RemoveSVG />,
              onClick: () => deleteOrRemoveTrack(true),
              warn: true,
            };
          }

          if (action === DEFAULT_TRACK_ACTION.DOWNLOAD_DEFAULT_VERSION) {
            // downloadTrackFiles requires the canonical lists.
            if (!isCanonicalTrack(track)) return null;
            return {
              name: "Download Default Version",
              icon: <DownloadSVG />,
              onClick: () => handleDownloadTrackFiles("default"),
            };
          }

          if (action === DEFAULT_TRACK_ACTION.DOWNLOAD_ALL_VERSIONS) {
            if ((track.versionFilesV2 || []).length > 1) {
              return {
                disabled: Boolean(trackDownloadRequest) || isLoading,
                disabledReason: "We're processing this download.",
                name: "Download All Versions",
                icon: <DownloadSVG />,
                onClick: () => handleDownloadTrackFiles("all"),
              };
            }

            return null;
          }

          if (action === DEFAULT_TRACK_ACTION.MANAGE_VERSIONS) {
            // The version editor needs the canonical version list. A
            // row-only tile carries `versionCount` but not the ids, so the
            // editor has nothing to manage — hide the item rather than open
            // it empty. Same `return null` convention as
            // DOWNLOAD_ALL_VERSIONS above.
            if (!isCanonicalTrack(track)) return null;
            return {
              name: "Manage Versions",
              icon: <WaveformSVG />,
              onClick: () =>
                openTrackVersionEditor({
                  track,
                  isBorrowed,
                  type: "default",
                  viewingSpaceId,
                }),
            };
          }

          return action;
        })}
      />
      {renderConfirmation}
    </ErrorBoundary>
  );
};

export const DefaultTrackActions = ({
  track,
  buttonProps,
  menuProps,
  hideTriggerButton,
  viewingSpaceId,
}: {
  track: RenderableTrack;
  buttonProps?: ButtonV2Props;
  menuProps?: Partial<MenuProps>;
  hideTriggerButton?: boolean;
  viewingSpaceId?: ViewingSpaceId;
}) => {
  const { isAllowed } = useAuth();
  const { getGlobalSpace } = useGlobalSpaces();
  const [parentSpace, setParentSpace] = useState<Space | undefined>();

  // The space whose permissions decide this menu is the one the track is
  // being VIEWED in. For a borrowed track that is the borrowing space (its
  // manager gets Remove; its origin is usually a space the viewer cannot
  // even read, and fetching it logged an error per tile). A plain `Track`
  // with no viewing space (Library, Search) falls back to its origin, as
  // before. A row-only tile with neither has nothing to fetch.
  const resolvedViewingSpaceId = viewingSpaceId ?? track.viewingSpaceId;
  const permissionSpaceId = resolvedViewingSpaceId ?? track.spaceId;

  useEffect(() => {
    let isMounted = true;

    if (isMounted && permissionSpaceId) {
      const globalSpace = getGlobalSpace(permissionSpaceId);
      if (globalSpace) {
        setParentSpace(globalSpace);
      } else {
        // TODO(globalState): This will rarely be used, when we implement the
        // new global space state in SpaceHome. Can keep as a fallback.
        highnote
          .getSpace({ id: permissionSpaceId, fromCache: true })
          .then((space) => {
            setParentSpace(space);
          })
          .catch((error) => {
            // Track you uploaded can belong to a space you don't have access to.
            // ie. User 1 has a space, and makes User 2 admin. User 2 creates their own space
            // that User 1 is not apart of yet, and moves a track/entity from User 1 Space -> User 2 Space.
            // User 1 will get an error for fetching space data of that new track's location.
            console.error("Error getting parent space", error);
          });
      }
    }

    return () => {
      isMounted = false;
    };
  }, [permissionSpaceId]);

  const authId = getAuthId();
  const actions = useMemo(() => {
    return getDefaultTrackActions({
      isTrackOwner: track.createdBy === authId,
      canManageSpace: isAllowed(PERMISSION.TO_MANAGE_SPACE, {
        space: parentSpace,
      }),
    });
  }, [isAllowed, track, authId, parentSpace]);

  return (
    <TrackActions
      track={track}
      actions={actions}
      parentSpace={parentSpace}
      viewingSpaceId={resolvedViewingSpaceId}
      buttonProps={buttonProps}
      menuProps={menuProps}
      hideTriggerButton={hideTriggerButton}
    />
  );
};
